import React from 'react';
import { cn } from '@/lib/utils';
import { Testimonial } from '@/data/testimonials';

interface TestimonialCardProps {
  testimonial: Testimonial;
  className?: string;
}

export const TestimonialCard: React.FC<TestimonialCardProps> = ({
  testimonial,
  className,
}) => {
  const {
    name,
    business,
    testimonial: quote,
    photo,
    metrics,
  } = testimonial;

  // Get initials for fallback
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  // Don't truncate quote - show full testimonial
  const fullQuote = quote;

  // Show only the first metric
  const primaryMetric = metrics[0];

  return (
    <div
      className={cn(
        'relative bg-white/95 backdrop-blur-sm rounded-xl',
        'border-2 border-teal-200/80 shadow-lg hover:shadow-xl',
        'p-6 flex-shrink-0 w-96 transition-all duration-300',
        'group overflow-hidden min-h-[200px]',
        'hover:border-teal-300/90 hover:-translate-y-1',
        className
      )}
    >
      {/* Enhanced hover gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-500/3 via-emerald-500/3 to-cyan-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />

      <div className="relative z-10 flex flex-col h-full">
        {/* Header with real image */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative h-12 w-12 flex-shrink-0">
            <img
              src={photo}
              alt={name}
              className="h-12 w-12 rounded-full object-cover border-2 border-teal-200 shadow-sm"
              onError={(e) => {
                // Fallback to initials if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            {/* Fallback initials */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-semibold rounded-full flex items-center justify-center text-sm"
              style={{ display: 'none' }}
            >
              {initials}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-base truncate">{name}</h3>
            <p className="text-sm text-teal-600 font-medium truncate">{business}</p>
          </div>
        </div>

        {/* Quote - Full length, auto-expanding */}
        <blockquote className="mb-4 flex-1">
          <p className="text-slate-600 text-sm leading-relaxed italic">
            &ldquo;{fullQuote}&rdquo;
          </p>
        </blockquote>

        {/* Single Key Metric */}
        {primaryMetric && (
          <div className="flex items-center justify-between bg-teal-50/80 rounded-lg px-3 py-2 mt-auto border border-teal-100">
            <span className="text-xs text-slate-600 font-medium">{primaryMetric.label}</span>
            <span className="text-sm text-teal-600 font-bold">{primaryMetric.value}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialCard;